export const TOUR_IDS = {
    GENERAL: 'general',
    LIBRARY: 'library',
    MATERIAL: 'material',
    MATERIAL_VIEW_SWITCHER: 'materialViewSwitcher',
    PLANNER: 'planner',
    PROGRESS: 'progress',
    RESULTS: 'results',
    AUTHOR: 'author'
}

// From the retired StartOfSchoolYearModal - kept untranslated/nl_NL-only, same as before.
const WELCOME_WEBINAR_AND_SUPPORT_HTML_NL = '<br><br>Volg een van onze gratis webinars om uitleg ' +
    'te krijgen over het beginnen met Learnbeat, de studieplanner inzetten, digitaal toetsen en ' +
    'meer. <a href="https://calendly.com/support-webinar" target="_blank">Meld je hier aan</a>.' +
    '<br><br>Via <a href="https://support.learnbeat.nl" target="_blank">support.learnbeat.nl</a> ' +
    'geven we zoveel mogelijk antwoorden op je vragen. Kom je er zelf niet uit? Neem dan contact ' +
    'met ons op via <a href="https://support.learnbeat.nl/article/559ik4uu2m-de-supporttoeter" ' +
    'target="_blank">de supporttoeter</a>, per mail aan ' +
    '<a href="mailto:support@learnbeat.nl">support@learnbeat.nl</a> of telefonisch op ' +
    '<a href="tel:+31207009854">020-7009854</a>.<br><br>Eva en Anne<br>Team Support'

// The support site (support.learnbeat.nl) is Dutch-only, hence nl_NL-only here too - same as
// WELCOME_WEBINAR_AND_SUPPORT_HTML_NL above.
const DEVELOPMENT_CLASS_SUPPORT_LINK_HTML_NL = '<br><br><a href="https://support.learnbeat.nl/' +
    'article/b0xte4v3ws-gebruik-een-ontwikkelklas-voor-het-maken-van-eigen-lesmateriaal" ' +
    'target="_blank">Lees meer over ontwikkelklassen</a>'

/**
 * Builds the tour registry. This is a function rather than a module-level constant because it
 * depends on window.i18n (and the market-4 leerling->student replacement wired up in main.js),
 * neither of which are guaranteed to exist yet at module-evaluation time.
 *
 * @returns {Object} map of tour id to tour definition
 */
function buildDefinitions() {
    return {

        // First login: what Learnbeat is, the first thing to do, where the main areas and help
        // live. Triggered from Home.js once the teacher's cards have rendered.
        [TOUR_IDS.GENERAL]: {
            version: 1,
            allowActiveInteraction: true,
            doneBtnText: window.i18n.gettext('Get started'),
            steps: [
                {
                    popover: {
                        title: window.i18n.gettext('Welcome to Learnbeat'),

                        // The nl_NL-only webinar/support block below replaces the old
                        // StartOfSchoolYearModal (removed from Layout.js), which showed this same
                        // content - untranslated, Dutch-market-only - as a separate first-login
                        // popup. Kept untranslated here too, matching its original scope.
                        description: window.i18n.gettext(
                            'Here you organize your classes, learning material and the progress of your students.'
                        ) + (LANGUAGE === 'nl_NL' ? WELCOME_WEBINAR_AND_SUPPORT_HTML_NL : '')
                    }
                },
                {
                    element: '.js-primary-navigation',
                    popover: {
                        title: window.i18n.gettext('Your main navigation'),
                        description: window.i18n.gettext(
                            'Each area explains itself the first time you open it.'
                        ),
                        side: 'right'
                    }
                },
                {
                    element: '.js-add-group-card',
                    condition: () => document.querySelector('.js-add-group-card') !== null,

                    // No "Next" - forward only via the highlighted button (paused for via
                    // continueOnNavigate) or "End tour".
                    data: { continueOnNavigate: true },
                    popover: {
                        title: window.i18n.gettext('Start with your first class'),
                        description: window.i18n.gettext(
                            'Almost everything in Learnbeat starts from a class. Click the button to add your own.'
                        ),
                        side: 'top',
                        showButtons: []
                    }
                },
                {
                    element: '.js-settings-link',
                    popover: {
                        title: window.i18n.gettext('Manage classes'),
                        description: window.i18n.gettext(
                            'This is where you can change your classes and add/manage students and teachers.'
                        ),
                        side: 'bottom',
                        align: 'start'
                    }
                },
                {
                    element: '[data-menu-item="settings"]',
                    popover: {
                        title: window.i18n.gettext('Invite others and manage your account'),
                        description: window.i18n.gettext(
                            'Inviting other teachers or managing your preferences is done here.'
                        ),
                        side: 'bottom',
                        align: 'start'
                    }
                },
                {
                    element: '[data-menu-item="support"]',
                    popover: {
                        title: window.i18n.gettext('Need help?'),
                        description: window.i18n.gettext(
                            'Support is always here if you get stuck. You can also replay these tips from there.'
                        ),
                        side: 'right'
                    }
                }
            ]
        },

        // Triggered on first visit to the Library home page. Kept to a single page on purpose -
        // opening an item and adding it to a class is explained rather than highlighted live.
        [TOUR_IDS.LIBRARY]: {
            version: 1,
            requires: [TOUR_IDS.GENERAL],

            // Nothing to add material to yet without a class - wait until one exists.
            condition: () => Backbone.Collection.groups.length > 0,
            steps: [
                {
                    popover: {
                        title: window.i18n.gettext('Library'),
                        description: window.i18n.gettext(
                            'In the Library you find learning material and add it to your classes. ' +
                            'All learning material that is visible, is available for you to use.'
                        )
                    }
                },
                {
                    element: '.js-sidepanel',
                    condition: () => document.querySelector('.js-sidepanel') !== null,
                    popover: {
                        title: window.i18n.gettext('Filters'),
                        description: window.i18n.gettext(
                            'Find learning material for you using these filters, we prefill subject, grade and level automatically.'
                        ),
                        side: 'right'
                    }
                },
                {
                    element: '.js-result-view',
                    popover: {
                        title: window.i18n.gettext('Search results'),
                        description: window.i18n.gettext(
                            'Here you find material from publishers, and material made by you or others. Open an ' +
                            'item to see what is in it and add the chapters you want to your classes.'
                        ),
                        side: 'top'
                    }
                }
            ]
        },

        // Triggered on groups/show, once the class actually has material to look at.
        [TOUR_IDS.MATERIAL]: {
            version: 1,
            steps: [
                {
                    element: '.js-chapters',
                    popover: {
                        title: window.i18n.gettext('Structure of your material'),
                        description: window.i18n.gettext(
                            'Learning material is built up from chapter to section to activity to exercises.'
                        ),
                        side: 'top'
                    }
                },
                {
                    element: '.js-page-buttons',
                    popover: {
                        title: window.i18n.gettext('Options'),
                        description: window.i18n.gettext(
                            'Search for specific material here, and decide what students can see and when ' +
                            'answers become available.'
                        ),
                        side: 'bottom',
                        align: 'end'
                    }
                },
                {
                    element: '.js-group-button',
                    popover: {
                        title: window.i18n.gettext('Switch class'),
                        description: window.i18n.gettext(
                            'Use this to change which class\'s learning material you are viewing.'
                        ),
                        side: 'right'
                    }
                }
            ]
        },

        // Extra one-off step, shown the first time a teacher reaches a chapter or section
        // level page, after the base "material" tour has already been seen.
        [TOUR_IDS.MATERIAL_VIEW_SWITCHER]: {
            version: 1,
            requires: [TOUR_IDS.MATERIAL],
            steps: [
                {
                    element: '.js-crumblepath-buttons',
                    popover: {
                        title: window.i18n.gettext('Switch view'),
                        description: window.i18n.gettext(
                            'Use these buttons to switch between an overview of the material, progress (what have ' +
                            'your students already done with it?), and author mode to edit it.'
                        ),
                        side: 'bottom',
                        align: 'start'
                    }
                },
                {
                    element: '.js-crumblepath',
                    popover: {
                        title: window.i18n.gettext('Navigating material'),
                        description: window.i18n.gettext(
                            'You can always click a level of the crumblepath to navigate directly to that layer.'
                        ),
                        side: 'bottom',
                        align: 'start'
                    }
                }
            ]
        },

        // Triggered on first visit to the study planner.
        [TOUR_IDS.PLANNER]: {
            version: 1,
            steps: [
                {
                    element: '.js-group-button',
                    popover: {
                        title: window.i18n.gettext('Choose a class'),
                        description: window.i18n.gettext(
                            'Choose which class you want to make a planning for.'
                        ),
                        side: 'right'
                    }
                },
                {
                    element: '.js-week-card .js-add-button',
                    popover: {
                        title: window.i18n.gettext('Fill your weeks'),
                        description: window.i18n.gettext(
                            'Add lessons, assignments or tests for each week.'
                        ),
                        side: 'top'
                    }
                },
                {
                    popover: {
                        title: window.i18n.gettext('Link learning material'),
                        description: window.i18n.gettext(
                            'Link the matching learning material so students can start it directly from their planning.'
                        )
                    }
                },
                {
                    element: '.js-planner-visibility-button',
                    popover: {
                        title: window.i18n.gettext('Turn the study planner on'),
                        description: window.i18n.gettext(
                            'Important: turn the study planner on once it is ready. Until then, students cannot see it.'
                        ),
                        side: 'bottom',
                        align: 'end'
                    }
                },
                {
                    element: '.js-planner-options-button',
                    popover: {
                        title: window.i18n.gettext('Share your work'),
                        description: window.i18n.gettext(
                            'You can easily reuse a study planner for your own or someone else’s classes. Click here to share a planner or use a planner that’s already been shared.'
                        ),
                        side: 'bottom',
                        align: 'end'
                    }
                }
            ]
        },

        // Triggered on first visit to the progress overview.
        [TOUR_IDS.PROGRESS]: {
            version: 1,
            steps: [
                {
                    popover: {
                        title: window.i18n.gettext('What Progress is for'),
                        description: window.i18n.gettext(
                            'Use Progress to see what students have done and to look at their work.'
                        )
                    }
                },
                {
                    element: '.progress-bar',
                    popover: {
                        title: window.i18n.gettext('Progress per student'),
                        description: window.i18n.gettext(
                            'Compare progress and scores within the class here.'
                        ),
                        side: 'top'
                    }
                },
                {
                    element: '.js-student-tree .js-tree-item:first-child .js-current-item',
                    condition: () => document.querySelector(
                        '.js-student-tree .js-tree-item:first-child .js-current-item'
                    ) !== null,
                    popover: {
                        title: window.i18n.gettext('Click through to the work'),
                        description: window.i18n.gettext(
                            'Click through to view answers, grade them and give feedback.'
                        ),
                        side: 'right'
                    }
                }
            ]
        },

        // Triggered on first visit to the results overview. Deliberately written to contrast
        // with Progress, which the teacher will typically have already seen by this point.
        [TOUR_IDS.RESULTS]: {
            version: 1,
            steps: [
                {
                    popover: {
                        title: window.i18n.gettext('Results versus Progress'),
                        description: window.i18n.gettext(
                            'Progress is about what students have already done. Results gives you the bigger ' +
                            'picture of how they are doing.'
                        )
                    }
                },
                {
                    element: '.js-results',
                    popover: {
                        title: window.i18n.gettext('Graph or list'),
                        description: window.i18n.gettext(
                            'Compare students with each other, or view the results as a list.'
                        ),
                        side: 'top'
                    }
                },
                {
                    element: '.js-results-filter-button',
                    popover: {
                        title: window.i18n.gettext('Zoom in with filters'),
                        description: window.i18n.gettext(
                            'Zoom in on, for example, learning material, topic, level or a date range.'
                        ),
                        side: 'bottom',
                        align: 'end'
                    }
                },
                {
                    element: '.js-results-list > *:first-child',
                    condition: () => document.querySelector('.js-results-list > *:first-child') !== null,
                    popover: {
                        title: window.i18n.gettext('Personal learning profile'),
                        description: window.i18n.gettext(
                            'Click a student for their personal learning profile.'
                        ),
                        side: 'right'
                    }
                }
            ]
        },

        // Triggered only on chapters/author and sections/author (not groups/, activities/ or
        // task_groups/author, even though they share the same base view).
        [TOUR_IDS.AUTHOR]: {
            version: 1,
            steps: [
                {
                    popover: {
                        title: window.i18n.gettext('What Author is for'),
                        description: window.i18n.gettext(
                            'Here you create and edit your own learning material.'
                        )
                    }
                },
                {
                    element: '.js-group-button',
                    popover: {
                        title: window.i18n.gettext('Use a development class'),
                        description: window.i18n.gettext(
                            'For new material, it is best to work in a separate development class.'
                        ) + (LANGUAGE === 'nl_NL' ? DEVELOPMENT_CLASS_SUPPORT_LINK_HTML_NL : ''),
                        side: 'right'
                    }
                },
                {
                    element: '.js-sortable-holder',
                    popover: {
                        title: window.i18n.gettext('Structure of your material'),
                        description: window.i18n.gettext(
                            'Your material consists of chapters, sections, activities and exercises. This page ' +
                            'manages one layer at a time - drag items to reorder them.'
                        ),
                        side: 'top'
                    }
                },
                {
                    element: '.js-actionbutton',

                    // The action button is a persistent, app-wide singleton that is only made
                    // visible a beat after this page renders (see List.js), so check visibility
                    // rather than mere presence.
                    condition: () => $('.js-actionbutton').is(':visible'),
                    popover: {
                        title: window.i18n.gettext('Add content'),
                        description: window.i18n.gettext(
                            'Add new content here.'
                        ),
                        side: 'left'
                    }
                },
                {
                    element: '.js-share-in-library-button',
                    condition: () => document.querySelector('.js-share-in-library-button') !== null,
                    popover: {
                        title: window.i18n.gettext('Check and publish'),
                        description: window.i18n.gettext(
                            'Check your material via Learning material. Ready? Place it in the Library from ' +
                            'Author to use or share it.'
                        ),
                        side: 'bottom',
                        align: 'end'
                    }
                }
            ]
        }

    }
}

export function getTourDefinition(tourId) {
    return buildDefinitions()[tourId]
}

export function getAllTourIds() {
    return Object.values(TOUR_IDS)
}
